import React, { Fragment } from "react";
import {
  Spin,
  Row,
  Col,
  Card as AntdCard,
  Statistic,
  Divider,
  Select,
} from "antd";
import Card from "../../components/Card";
import { Bar } from "react-chartjs-2";
import { User, Opportunity } from "../../helpers/shared-interfaces";
import { generateGreeting, request } from "../../helpers/utilities";
import EntitledSection from "../../components/EntitledSection";
import entitlements from "../../config/entitlements";
import { AxiosResponse } from "axios";
import { RouteComponentProps } from "react-router-dom";

const GET_USER_OPPORTUNITIES = `
  query getUserOpportunities($forCurrentUser: Boolean) {
    wonOpportunities(forCurrentUser: $forCurrentUser) {
      id
    }
    inProgressOpportunities(forCurrentUser: $forCurrentUser) {
      id
    }
    leadOpportunities(forCurrentUser: $forCurrentUser) {
      id
    }
  }
`;

const { Option } = Select;

const Chartcolors = [
  "rgb(45, 159, 124)",
  "rgb(37, 191, 88)",
  "rgb(177, 191, 37)",
  "rgb(74, 108, 152)",
  "rgb(18, 203, 166)",
  "rgb(76, 30, 228)",
  "rgb(203, 18, 191)",
  "rgb(203, 172, 18)",
  "rgb(229, 105, 10)",
  "rgb(229, 54, 10)",
];

const GET_INTERACTIONS_SUMMARY = `
  query getInteractionsSummary($forCurrentUser: Boolean) {
    opportunityInteractionSummary(forCurrentUser: $forCurrentUser) {
      month
      summary {
          interactionMode
          count
      }
    }
  }
`;

enum ViewTypes {
  AllActions = "All Actions",
  AssignedToMe = "Assigned To Me",
}

interface Action {
  title: string;
  value: number;
}

interface InteractionMode {
  interactionMode: string;
  count: string;
}

interface InteractionSummary {
  month: string;
  summary: InteractionMode[];
}

interface ChartDataSet {
  label: string;
  backgroundColor?: string;
  borderColor?: string;
  data: number[];
}

interface ChartData {
  labels: string[];
  datasets: ChartDataSet[];
}

export interface ObjectLiteral {
  [key: string]: number[];
}

const chartOptions = {
  showScale: true,
  pointDot: true,
  showLines: false,

  legend: {
    display: true,
    position: "bottom",
  },

  scales: {
    yAxes: [
      {
        ticks: {
          beginAtZero: true,
          min: 0,
        },
      },
    ],
  },
};

interface OpportunityMetricsViewProps {
  prospective: number;
  inProgress: number;
  won: number;
  passFilterInUrl: (filter: string) => void;
}

const opportunityMetricsArray: Action[] = [
  {
    title: "Prospective Opportunities",
    value: 0,
  },
  {
    title: "Opportunities In Progress",
    value: 0,
  },
  {
    title: "Opportunities Won",
    value: 0,
  },
];

export function OpportunityMetricsView(
  props: OpportunityMetricsViewProps,
): JSX.Element {
  return (
    <Row gutter={16}>
      {opportunityMetricsArray.map((action: Action): JSX.Element => {
        let value = 0;
        let myID = 0;
        if (action.title === "Prospective Opportunities") {
          value = props.prospective;
          myID = 1;
        } else if (action.title === "Opportunities In Progress") {
          value = props.inProgress;
          myID = 2;
        } else {
          value = props.won;
          myID = 3;
        }
        return (
          <Col span={8} key={action.title}>
            <AntdCard
              className="opportunity-metric-card"
              id={`opportunity-metric-card-${myID}`}
              onClick={(): void => {
                props.passFilterInUrl(action.title);
              }}
            >
              <Statistic title={action.title} value={value} />
            </AntdCard>
          </Col>
        );
      })}
    </Row>
  );
}

export function TransformInteractionSummary(
  interactionsSummary: InteractionSummary[],
): ChartData {
  const temporal: ObjectLiteral = {};
  const labels: string[] = [];
  const datasets: ChartDataSet[] = [];
  const modes: string[] = [];

  for (const interactionSummary of interactionsSummary) {
    for (const interactionMode of interactionSummary.summary) {
      if (modes.indexOf(interactionMode.interactionMode) === -1) {
        modes.push(interactionMode.interactionMode);
        temporal[interactionMode.interactionMode] = [];
      }
    }
  }

  for (const interactionSummary of interactionsSummary) {
    labels.push(interactionSummary.month);
    for (const key in temporal) {
      let check = false;
      for (const interactionMode of interactionSummary.summary) {
        const value = parseFloat(interactionMode.count);
        if (key === interactionMode.interactionMode) {
          temporal[key].push(value);
          check = true;
        }
      }
      if (!check) {
        temporal[key].push(0);
      }
    }
  }

  const data: ChartData = { labels, datasets };
  let count = 0;
  for (const key in temporal) {
    data.datasets.push({
      label: key,
      data: temporal[key],
      backgroundColor: Chartcolors[count],
      borderColor: Chartcolors[count],
    });
    count++;
  }
  return data;
}

export interface dashboard {
  department: string;
  component: typeof React.Component;
}

export interface dashboardSelectorProps {
  dashboards: dashboard[];
  user: User;
}

interface State {
  viewToRender: string;
  summaryData: InteractionSummary[];
  transformedData: ChartData;
  isLoading: boolean;
  wonOpportunitiesCount: number;
  inProgressOpportunitiesCount: number;
  prospectiveOpportunitiesCount: number;
}

interface Props extends RouteComponentProps {
  user: User;
}

export default class BusinessDevelopmentDashboard extends React.Component<
  Props,
  State
> {
  _isMounted = false;
  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      isLoading: true,
      viewToRender: ViewTypes.AllActions,
      summaryData: [],
      transformedData: { labels: [], datasets: [] },
      wonOpportunitiesCount: 0,
      inProgressOpportunitiesCount: 0,
      prospectiveOpportunitiesCount: 0,
    };
    this.getOpportunities = this.getOpportunities.bind(this);
  }

  onClick = (view: string): void => {
    this.setState({
      viewToRender: view,
    });
    if (view === "All Actions") {
      this.getInteractionsSummary();
      this.getOpportunities();
    } else {
      this.getInteractionsSummary(true);
      this.getOpportunities(true);
    }
  };

  async getInteractionsSummary(forCurrentUser = false): Promise<void> {
    this.setState({ isLoading: true });
    try {
      const { data }: any = await request({
        query: GET_INTERACTIONS_SUMMARY,
        variables: { forCurrentUser },
      });
      const opportunityInteractionSummary =
        data.data.opportunityInteractionSummary;
      const transformedData: ChartData = TransformInteractionSummary(
        opportunityInteractionSummary,
      );
      this.setState({
        summaryData: opportunityInteractionSummary,
        transformedData,
      });
    } catch (error) { }
    finally {
      this.setState({ isLoading: false });
    }
  }

  async getOpportunities(forCurrentUser = false): Promise<void> {
    interface Response {
      data: {
        leadOpportunities: Opportunity[];
        wonOpportunities: Opportunity[];
        inProgressOpportunities: Opportunity[];
      };
    }
    this.setState({ isLoading: true });

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_USER_OPPORTUNITIES,
        variables: { forCurrentUser },
      });

      const { leadOpportunities, inProgressOpportunities, wonOpportunities } =
        data;

      this.setState({
        prospectiveOpportunitiesCount: leadOpportunities.length,
        inProgressOpportunitiesCount: inProgressOpportunities.length,
        wonOpportunitiesCount: wonOpportunities.length,
        isLoading: false,
      });
    } catch (error) { }
  }

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    await this.getOpportunities();
    await this.getInteractionsSummary();
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  passFilterInUrl = (filter: string): void => {
    const view = this.state.viewToRender;
    this.props.history.push(
      `/business-development?view=${view}&filter=${filter}`,
    );
  };

  render(): JSX.Element | null | typeof React.Component | undefined {
    const hour = new Date().getHours();
    const {
      prospectiveOpportunitiesCount,
      inProgressOpportunitiesCount,
      wonOpportunitiesCount,
      isLoading,
    } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <Row
              style={{
                marginBottom: "17px",
              }}
            >
              <Col span={12}>
                <h3>
                  {generateGreeting(hour)}, {this.props.user.name}
                </h3>
              </Col>
              <EntitledSection
                entitlement={entitlements.BusinessDevelopment.metrics}
              >
                <Col
                  span={12}
                  style={{
                    textAlign: "right",
                  }}
                >
                  <Select
                    style={{
                      width: "140px",
                    }}
                    key={this.state.viewToRender}
                    showSearch
                    placeholder="View To Display"
                    onChange={this.onClick}
                    value={this.state.viewToRender}
                  >
                    <Option key={1} value={ViewTypes.AllActions}>
                      {ViewTypes.AllActions}
                    </Option>
                    <Option key={2} value={ViewTypes.AssignedToMe}>
                      {ViewTypes.AssignedToMe}
                    </Option>
                  </Select>
                </Col>
              </EntitledSection>
            </Row>
            <EntitledSection
              entitlement={entitlements.BusinessDevelopment.metrics}
            >
              <OpportunityMetricsView
                prospective={prospectiveOpportunitiesCount}
                inProgress={inProgressOpportunitiesCount}
                won={wonOpportunitiesCount}
                passFilterInUrl={this.passFilterInUrl}
              />
            </EntitledSection>

            <Divider />

            <Row gutter={16}>
              <Col span={24}>
                <EntitledSection
                  entitlement={
                    entitlements.BusinessDevelopment.interactions.summary
                  }
                >
                  <AntdCard>
                    <p>Interactions Summary</p>
                    <Bar
                      data={this.state.transformedData}
                      width={20}
                      height={5}
                      options={chartOptions}
                    />
                  </AntdCard>
                </EntitledSection>
              </Col>
            </Row>
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
